import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Phone,
  Shield,
  Clock,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { Button } from "./ui/button";
import { BRAND, MEDIA, whatsappLink } from "../data/brand";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 sm:pt-32 lg:pt-36"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute inset-0 hero-grid-pattern" />
      <div className="pointer-events-none absolute -top-20 -left-20 h-72 w-72 rounded-full bg-[#1E3A8A] blob" />
      <div className="pointer-events-none absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-[#EA580C] blob" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-16 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-24">
        {/* Left: Copy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-700 backdrop-blur">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-[#EA580C]/10 text-[#EA580C]">
              <Sparkles className="h-3 w-3" />
            </span>
            Centro Quirúrgico Veterinario · Copiapó
          </div>

          <h1
            data-testid="hero-title"
            className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tight text-slate-900 text-balance sm:text-5xl lg:text-6xl xl:text-7xl"
          >
            Cuidado{" "}
            <span className="relative inline-block">
              <span className="relative z-10 font-medium text-[#1E3A8A]">
                veterinario
              </span>
              <span className="absolute inset-x-0 bottom-1 -z-0 h-3 rounded-full bg-[#EA580C]/20" />
            </span>{" "}
            profesional y humano.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Atención médica, cirugía y urgencias para mascotas. En CEQVet
            combinamos experiencia clínica, tecnología y un trato cercano para
            cuidar a tu compañero como se merece.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/reserva">
              <Button
                data-testid="hero-reservar-btn"
                size="lg"
                className="rounded-full bg-[#1E3A8A] px-6 text-white shadow-lg shadow-blue-900/15 hover:bg-[#172554]"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Reservar hora
                <ChevronRight className="ml-1 h-4 w-4 opacity-70" />
              </Button>
            </Link>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
            >
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-slate-300 bg-white px-6 text-slate-900 hover:bg-slate-50"
              >
                <Phone className="mr-2 h-4 w-4 text-[#25D366]" />
                WhatsApp
              </Button>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-5 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[#1E3A8A]/8 text-[#1E3A8A]">
                <Shield className="h-4 w-4" />
              </span>
              Equipo titulado
            </div>
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
                <Sparkles className="h-4 w-4" />
              </span>
              Tecnología actual
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[#EA580C]/10 text-[#EA580C]">
                <Clock className="h-4 w-4" />
              </span>
              Atención oportuna
            </div>
          </div>
        </motion.div>

        {/* Right: Media */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
          className="relative lg:col-span-5"
        >
          <div className="relative overflow-hidden rounded-3xl border border-white/60 bg-white shadow-2xl shadow-blue-900/10">
            <img
              src={MEDIA.hero}
              alt="Veterinario atendiendo un perro en clínica moderna"
              className="aspect-[4/5] w-full object-cover"
              loading="eager"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
          </div>

          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="absolute -bottom-6 -left-6 hidden w-60 rounded-2xl glass p-4 sm:block"
          >
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#1E3A8A] text-white">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Equipo titulado
                </div>
                <div className="text-xs text-slate-600">
                  Profesionales con experiencia
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="absolute -top-5 right-2 hidden rounded-full glass px-4 py-2 text-xs font-medium text-slate-700 sm:block"
          >
            ⭐ 4.9 valoración de tutores
          </motion.div>
        </motion.div>
      </div>

      {/* Trust strip */}
      <div className="relative border-y border-slate-100 bg-white/60">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5 text-xs uppercase tracking-[0.18em] text-slate-500 lg:px-12">
          <span>Medicina · Cirugía · Hospitalización</span>
          <span className="hidden sm:inline">Ecografía · Laboratorio</span>
          <span>Vacunación · Traumatología</span>
          <span className="hidden sm:inline">{BRAND.city}</span>
        </div>
      </div>
    </section>
  );
}
